/**
 * CSAGE-310: the single place that owns how the SPA isolates HTML it did
 * not author — presentation decks (arbitrary markup a PresentationAuthor
 * uploads) and rendered report HTML served as `blob:` URLs.
 *
 * A `blob:` URL inherits the origin of the document that created it — the
 * SPA's own origin. Granting such a frame `sandbox="allow-scripts
 * allow-same-origin"` lets the framed (attacker-influenceable) document
 * reach `parent.document`, `parent.localStorage`, and the live React tree
 * — including any in-memory session token — because `allow-same-origin`
 * on a same-origin `blob:` document restores the creating document's
 * origin, and the HTML standard lets a document with both permissions
 * remove its own sandbox entirely.
 *
 * Rule: NEVER pair `allow-same-origin` with a blob-sourced (or otherwise
 * untrusted-content) iframe. `UNTRUSTED_FRAME_SANDBOX` /
 * `UNTRUSTED_FRAME_SANDBOX_WITH_MODALS` below are the only sandbox
 * strings a call site should use for such a frame — they grant an opaque
 * origin, so `parent.document` access throws instead of succeeding.
 * `sandbox-scan.test.ts` (via `sandbox-policy.test.ts`) fails CI if a
 * future call site reintroduces the combination or ships an unsandboxed
 * blob iframe. Before adding a seventh frame, read this file first.
 *
 * Since the framed document is opaque-origin, the only way in or out is
 * `postMessage`. `isFrameMessageFrom` authenticates an inbound message by
 * `event.source` identity (the only thing that survives an opaque
 * origin — `event.origin` is the literal string "null" and carries no
 * identity). `clampFrameHeight` / `clampFrameWidth` bound a
 * guest-reported dimension so a hostile document can't force a
 * multi-million-pixel parent layout.
 */

export const UNTRUSTED_FRAME_SANDBOX = 'allow-scripts';
export const UNTRUSTED_FRAME_SANDBOX_WITH_MODALS = 'allow-scripts allow-modals';

export const MAX_FRAME_DIMENSION_PX = 200_000;

function clampDimension(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return fallback;
  return Math.min(value, MAX_FRAME_DIMENSION_PX);
}

export function clampFrameHeight(value: unknown, fallback: number): number {
  return clampDimension(value, fallback);
}

export function clampFrameWidth(value: unknown, fallback: number): number {
  return clampDimension(value, fallback);
}

/**
 * True only when `event.source` is exactly the given iframe's
 * `contentWindow`. This is the ONLY valid way to authenticate a message
 * from an untrusted frame — `event.origin` is the string `"null"` for an
 * opaque-origin document, which is not a meaningful identity to check
 * against.
 */
export function isFrameMessageFrom(
  event: Pick<MessageEvent, 'source'>,
  iframe: HTMLIFrameElement | null,
): boolean {
  if (!iframe) return false;
  const win = iframe.contentWindow;
  if (!win) return false;
  return event.source === win;
}

export interface SageFrameResizeMessage {
  readonly channel: 'sage-report';
  readonly type: 'resize';
  readonly payload: { readonly height?: number; readonly width?: number };
}

export type SageFrameMessage = SageFrameResizeMessage;

/**
 * Parses `event.data` into the typed resize message shape, or returns
 * null for anything else (wrong channel, wrong type, malformed, or not
 * an object at all). Never throws — a hostile or malformed guest message
 * is just ignored.
 */
export function parseSageFrameMessage(data: unknown): SageFrameMessage | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as { channel?: unknown; type?: unknown; payload?: unknown };
  if (d.channel !== 'sage-report') return null;
  if (d.type !== 'resize') return null;
  const rawPayload = (d.payload && typeof d.payload === 'object' ? d.payload : {}) as {
    height?: unknown;
    width?: unknown;
  };
  const payload: { height?: number; width?: number } = {};
  if (typeof rawPayload.height === 'number') payload.height = rawPayload.height;
  if (typeof rawPayload.width === 'number') payload.width = rawPayload.width;
  return { channel: 'sage-report', type: 'resize', payload };
}
